import { Button, Form, Input, Space, Typography, notification } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { UploadFile as UploadFileInterface } from 'antd/es/upload'
import { FC } from 'react'
import UploadFile from '../../form-items/upload-file/upload-file'
import extractCreateDrawingBlankTaskArg from '../../../helpers/tasks/extract-create-drawing-blank-task'
import isValidateErrorEntity from '../../../helpers/type-guards/is-validate-error-entity'
import useErrorModal from '../../../hooks/api/use-error-modal'
import useUploadFiles from '../../../hooks/api/use-upload-file'
import {
    useLazyGetShipmentByDocIdQuery,
    useUpdateShipmentMutation,
} from '../../../store/api/shipments-api'
import { useCreateTaskMutation } from '../../../store/api/tasks-api'
import { EndpointQueryParameters } from '../../../types/strapi/strapi'
import { TaskCreationFormProps } from '../types'

interface DrawingBlankFormValues {
    shipmentNumber?: string
    sourceFiles?: UploadFileInterface[]
    description?: string
}

const DRAWING_BLANK_SHIPMENT_QUERY: EndpointQueryParameters = {
    populate: {
        designer: {
            fields: ['documentId'],
        },
        sourceFile: {
            fields: ['id'],
        },
    },
}

const DrawingBlankForm: FC<TaskCreationFormProps<'Отрисовка бланка'>> = ({
    initialValues,
    onSuccess,
    onCancel,
}) => {
    const shipment = initialValues?.shipment
    const [form] = useForm<DrawingBlankFormValues>()
    const [getShipmentByDocId, getShipmentByDocIdState] = useLazyGetShipmentByDocIdQuery()
    const [updateShipment, updateShipmentState] = useUpdateShipmentMutation()
    const [createTask, createTaskState] = useCreateTaskMutation()
    const [uploadFiles, uploadState] = useUploadFiles()

    useErrorModal(getShipmentByDocIdState, 'Ошибка получения данных отгрузки')
    useErrorModal(updateShipmentState, 'Не удалось обновить отгрузку')
    useErrorModal(createTaskState, 'Ошибка создания задачи')

    const loading =
        getShipmentByDocIdState.isLoading ||
        getShipmentByDocIdState.isFetching ||
        uploadState.isLoading ||
        updateShipmentState.isLoading ||
        createTaskState.isLoading

    const onSubmit = async () => {
        try {
            const values = await form.validateFields()
            const shipmentDocId = shipment?.documentId

            if (!shipmentDocId) {
                notification.error({ message: 'Не передана отгрузка' })
                return
            }

            const fetchedResult = await getShipmentByDocId(
                [shipmentDocId, DRAWING_BLANK_SHIPMENT_QUERY],
                false
            )
            const fetchedShipment = fetchedResult.data?.data

            if (!fetchedShipment) {
                notification.error({ message: 'Не удалось получить данные отгрузки' })
                return
            }

            const existingIds = (fetchedShipment.sourceFile ?? []).map(file => file.id)
            let newIds: number[] = []
            const description = values.description?.trim()

            if (values.sourceFiles?.length) {
                const uploaded = await uploadFiles(values.sourceFiles)

                if (!uploaded.data?.length) {
                    notification.error({ message: 'Не удалось сохранить файл' })
                    return
                }

                newIds = uploaded.data.map(file => file.id)
            }

            if (newIds.length) {
                await updateShipment({
                    docId: fetchedShipment.documentId,
                    body: {
                        data: {
                            //@ts-ignore Strapi media multiple: массив numeric id
                            sourceFile: [...existingIds, ...newIds],
                        },
                    },
                }).unwrap()
            }

            await createTask(
                extractCreateDrawingBlankTaskArg(fetchedShipment, description)
            ).unwrap()
            notification.success({ message: 'Задача создана' })
            onSuccess()
        } catch (e) {
            if (isValidateErrorEntity(e)) return
            console.error(e)
            notification.error({ message: 'Не удалось создать задачу' })
        }
    }

    if (!shipment?.documentId) {
        return <Typography.Text type='danger'>Не передана отгрузка</Typography.Text>
    }

    return (
        <Form
            form={form}
            layout='vertical'
            initialValues={{
                shipmentNumber: shipment.id != null ? String(shipment.id) : undefined,
                sourceFiles: initialValues?.sourceFiles,
                description: initialValues?.description,
            }}
        >
            <Form.Item name='shipmentNumber' label='Номер отгрузки'>
                <Input disabled />
            </Form.Item>
            <UploadFile
                name='sourceFiles'
                label='Исходные файлы'
                multiple
               
            />
            <Form.Item name='description' label='Описание задачи'>
                <Input.TextArea rows={3} />
            </Form.Item>
            <Space>
                <Button onClick={onCancel}>Отмена</Button>
                <Button type='primary' loading={loading} onClick={onSubmit}>
                    Создать
                </Button>
            </Space>
        </Form>
    )
}

export default DrawingBlankForm
